import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchAutoRenewRequests, fetchAutoRenewRequestDetail, completeAutoRenewRequest } from "../utils/api";
import { fmtDateOnly, fmtInstant } from "../utils/dates";

const DEFAULT_FILTERS = { status: "needs_action", notification: "all", search: "" };
const TIME_OPTIONS = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago", timeZoneName: "short" };
const instant = value => fmtInstant(value, TIME_OPTIONS, "Unavailable");
const validCount = value => Number.isSafeInteger(value) && value >= 0;
const scopeStyles = `
.ar-request-management button, .ar-request-drawer button,
.ar-request-management select, .ar-request-management input:not([type=checkbox]) {
  font: inherit; font-size: 12px; color: var(--ar-text); background: var(--ar-control);
  border: 1px solid var(--ar-border); border-radius: 6px; padding: 6px 10px;
  max-width: 100%; box-sizing: border-box;
}
.ar-request-management button, .ar-request-drawer button { cursor: pointer; }
.ar-request-management button:hover:enabled, .ar-request-drawer button:hover:enabled {
  border-color: var(--ar-blue); background: rgba(68,166,255,.14);
}
.ar-request-management button:disabled, .ar-request-drawer button:disabled { opacity: .5; cursor: default; }
.ar-request-management :focus-visible, .ar-request-drawer :focus-visible { outline: 2px solid var(--ar-blue); outline-offset: 3px; }
.ar-request-management label { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.ar-request-management th { font-size: 11px; color: var(--ar-muted); font-weight: 600; }
.ar-request-management td { border-bottom: 1px solid var(--ar-border); vertical-align: top; }
.ar-request-management td small { margin-top: 4px; overflow-wrap: anywhere; }
.ar-request-drawer { font-family: inherit; font-size: 13px; line-height: 1.6; }
`;
const scopeTheme = theme => ({ "--ar-text": theme.text, "--ar-muted": theme.muted,
  "--ar-border": theme.border, "--ar-blue": theme.blue || "#44a6ff", "--ar-control": "#152338" });

// The badge is loaded when Customer Management opens, independently of which
// subtab/filter/page is selected. A missing backend/off answer is never zero.
export function useAutoRenewManagementCount(active) {
  const [count, setCount] = useState({ state: "loading", value: null });
  const generation = useRef(0);
  const receive = useCallback(data => {
    generation.current += 1;
    setCount(data?.enabled === false ? { state: "off", value: null } :
      data?.enabled === true && validCount(data.outstanding_count) ? { state: "ready", value: data.outstanding_count } :
        { state: "unavailable", value: null });
  }, []);
  const unavailable = useCallback(() => {
    generation.current += 1;
    setCount({ state: "unavailable", value: null });
  }, []);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const ticket = ++generation.current;
    setCount({ state: "loading", value: null });
    const load = async () => {
      try {
        const data = await fetchAutoRenewRequests(DEFAULT_FILTERS);
        if (!cancelled && ticket === generation.current) receive(data);
      } catch (_) {
        if (!cancelled && ticket === generation.current) unavailable();
      }
    };
    load();
    return () => { cancelled = true; };
  }, [active, receive, unavailable]);
  return { count, receive, unavailable };
}

export function requestCountLabel(count) {
  return count.state === "ready" ? `Auto-renew requests (${count.value})` :
    count.state === "loading" ? "Auto-renew requests (count loading…)" :
      count.state === "off" ? "Auto-renew requests (off)" : "Auto-renew requests (count unavailable)";
}

export function AutoRenewRequestDrawer({ requestId, theme, returnFocusTo, onClose, onLockChange, onCompleted }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [queueStale, setQueueStale] = useState(false);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const acknowledgementRef = useRef(null);
  const markCompletedRef = useRef(null);
  const receiptRef = useRef(null);
  const errorRef = useRef(null);
  const previousFocusPhase = useRef(null);
  const focusReturn = useRef(returnFocusTo);
  const saveLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const trigger = focusReturn.current;
    return () => {
      mounted.current = false;
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setDetail(null); setError(null); setConfirming(false); setAcknowledged(false); setReceipt(null); setQueueStale(false);
    fetchAutoRenewRequestDetail(requestId).then(data => {
      if (!cancelled) setDetail(data);
    }).catch(() => { if (!cancelled) setError("Request details are unavailable. Refresh before taking action."); });
    return () => { cancelled = true; };
  }, [requestId, refresh]);
  const focusPhase = receipt ? "receipt" : error ? "error" : saving ? "saving" : confirming ? "confirmation" : detail ? "read" : "loading";
  useLayoutEffect(() => {
    const previous = previousFocusPhase.current;
    previousFocusPhase.current = focusPhase;
    // Replaced controls cannot retain focus. Keep the next focus target inside
    // the drawer without moving it again for acknowledgement/queue rerenders.
    if (focusPhase === "confirmation") acknowledgementRef.current?.focus();
    else if (focusPhase === "receipt") receiptRef.current?.focus();
    else if (focusPhase === "error") errorRef.current?.focus();
    else if (focusPhase === "saving") dialogRef.current?.focus();
    else if (previous === "confirmation") (markCompletedRef.current || closeRef.current)?.focus();
    else if (!dialogRef.current?.contains(document.activeElement)) closeRef.current?.focus();
  }, [focusPhase]);
  useEffect(() => {
    onLockChange(confirming || saving);
    return () => onLockChange(false);
  }, [confirming, saving, onLockChange]);
  useEffect(() => {
    const handler = event => {
      if (event.key === "Escape" && !saveLock.current) {
        event.preventDefault(); onClose();
      }
      if (event.key === "Tab") {
        const controls = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]') || []);
        if (!controls.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); }
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  const save = async () => {
    if (!acknowledged || !confirming || saveLock.current || !detail?.can_complete || receipt) return;
    // Freeze the exact target and version at confirmation. The synchronous ref
    // also closes the double-click gap before React renders the saving state.
    const target = { id: detail.id, etag: detail.etag };
    saveLock.current = true; setSaving(true); setError(null);
    try {
      const result = await completeAutoRenewRequest(target.id, target.etag);
      if (!mounted.current) return;
      setReceipt(result); setConfirming(false);
      if (["recorded", "observed_completed", "already_completed"].includes(result.outcome)) {
        const fresh = await onCompleted();
        if (mounted.current) setQueueStale(!fresh);
      }
    } catch (failure) {
      if (mounted.current) {
        setConfirming(false); setAcknowledged(false);
        setError(failure?.status === 409 || failure?.status === 428 ?
          "This request or its term changed. Refresh the request before taking action." :
          "Completion could not be verified. Refresh the request before taking action.");
        // A lost HTTP response may follow a real write. Require an explicit
        // fresh detail read before offering another action.
        setDetail(current => current ? { ...current, can_complete: false } : current);
      }
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const displayed = receipt?.receipt || detail;
  const fact = (label, value) => <div style={{ marginBottom: 12 }}><dt style={{ color: theme.muted, fontSize: 12 }}>{label}</dt><dd style={{ margin: "3px 0 0", overflowWrap: "anywhere" }}>{value ?? "Unavailable"}</dd></div>;
  return createPortal(<div className="ar-request-drawer" style={{ ...scopeTheme(theme), position: "fixed", inset: 0, zIndex: 1600, background: "rgba(0,0,0,.55)" }} onClick={() => { if (!saveLock.current) onClose(); }}>
    <section ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="auto-renew-request-title"
      onClick={event => event.stopPropagation()} style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "min(560px, 96vw)", overflowY: "auto", padding: 24, boxSizing: "border-box", color: theme.text, background: theme.bg || "#101b2d", borderLeft: `1px solid ${theme.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <h3 id="auto-renew-request-title" style={{ marginTop: 0 }}>Auto-renew request</h3>
        <button ref={closeRef} disabled={saving} onClick={onClose} aria-label="Close auto-renew request">Close</button>
      </div>
      {!detail && !error && <p role="status">Loading request…</p>}
      {error && <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>}
      {receipt && <div ref={receiptRef} tabIndex={-1} role="status" style={{ margin: "12px 0", padding: 12, border: `1px solid ${theme.border}` }}>
        {receipt.outcome === "outcome_unverified" ? "Outcome unverified. No automatic retry was made. Refresh the request to check its recorded state." :
          receipt.outcome === "observed_completed" ? "Completion was observed. It cannot be attributed to this click." :
            receipt.outcome === "already_completed" ? "This request was already completed. Its original receipt is unchanged." :
              receipt.receipt ? "Completion recorded for the captured renewal." : "Completion was recorded, but its details could not be refreshed."}
        {receipt.term_observation && <p>Term observation: {receipt.term_observation.state === "matching" ? "matching when checked" :
          receipt.term_observation.state === "changed" ? "changed or expired — needs reconciliation" : "unavailable — needs reconciliation"} ({instant(receipt.term_observation.observed_on)}).</p>}
        {queueStale && <p>The request list and outstanding count could not be refreshed. The recorded receipt above is retained.</p>}
      </div>}
      {displayed && <>
        <h4>{displayed.customer}</h4>
        {!!displayed.needs_review?.length && <p>Needs review: {displayed.needs_review.join("; ")}</p>}
        <dl>
          {fact("Request number", displayed.request_number)}
          {fact("Recorded requester", `${displayed.requested_by_name || "Unavailable"} · ${displayed.requested_by_email || "Unavailable"}`)}
          {fact("Requested (America/Chicago)", instant(displayed.requested_on))}
          {fact("Captured term start", fmtDateOnly(displayed.term_start))}
          {fact("Captured last covered day", fmtDateOnly(displayed.term_end))}
          {fact("Requested next-term start", fmtDateOnly(displayed.effective_on))}
          {fact("Recorded offer", displayed.offer_text)}
          {fact("Recorded discount (next contract only)", displayed.discount_percent == null ? null : `${displayed.discount_percent}%`)}
          {fact("Request status", receipt?.outcome === "recorded" && !receipt.receipt ? "Completion recorded; details unavailable" : displayed.status)}
          {fact("Notification", displayed.notification)}
          {fact("Notification sent (America/Chicago)", instant(displayed.notification_sent_on))}
          {fact("Completed by", displayed.completed_by || (displayed.receipt_valid ? "Recorded staff member; name unavailable" : null))}
          {fact("Completed (America/Chicago)", instant(displayed.completed_on))}
        </dl>
        <p style={{ color: theme.muted, fontSize: 12 }}>Notification status is independent of contract paperwork. This request does not prove the contract was changed.</p>
      </>}
      {detail?.can_complete && !receipt && !confirming && <button ref={markCompletedRef} onClick={() => { setAcknowledged(false); setConfirming(true); }}>Mark completed</button>}
      {detail && !detail.can_complete && !receipt && <p>Read only. {detail.completion_unavailable_reason === "Read only" ? "Completion is not available for this account or mode." : "This request needs review before completion."}</p>}
      {confirming && <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 16 }}>
        <p>Complete {detail.customer}’s request for the renewal starting {fmtDateOnly(detail.effective_on)}.</p>
        <p>This records completion of the request. It does not change the contract or auto-renew setting, or send an email.</p>
        <p>If the term changes while saving, this request may need reconciliation and an offer for the newer term may become available.</p>
        <label style={{ display: "block", margin: "16px 0" }}><input ref={acknowledgementRef} type="checkbox" checked={acknowledged} disabled={saving} onChange={event => setAcknowledged(event.target.checked)} /> I confirm the signed paperwork for this next term is complete and includes the requested 5% savings.</label>
        <button disabled={!acknowledged || saving} onClick={save}>{saving ? "Recording…" : "Confirm completion"}</button>{" "}
        <button disabled={saving} onClick={() => { setConfirming(false); setAcknowledged(false); }}>Cancel</button>
      </div>}
      {!confirming && !saving && <p><button onClick={() => setRefresh(value => value + 1)}>Refresh request</button></p>}
    </section>
  </div>, document.body);
}

export default function AutoRenewRequestManagement({ theme, onCount, onCountUnavailable, onBusyChange }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState(null);
  const [history, setHistory] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const sequence = useRef(0);
  const rowTriggers = useRef(new Map());
  const refreshButtonRef = useRef(null);
  const fallbackFocusRef = useRef(null);
  const returnFocusId = useRef(null);
  useLayoutEffect(() => {
    if (selected || locked || returnFocusId.current === null) return;
    // Resolve the current DOM row after refresh, not the detached opener.
    // Loading/removal/error can remove every row or disable Refresh requests.
    const targets = [rowTriggers.current.get(returnFocusId.current), refreshButtonRef.current, fallbackFocusRef.current];
    targets.find(target => target?.isConnected && !target.disabled)?.focus();
    returnFocusId.current = null;
  }, [selected, locked, loading]);
  const setLock = useCallback(value => { setLocked(value); onBusyChange(value); }, [onBusyChange]);
  const load = useCallback(async (page = cursor) => {
    const ticket = ++sequence.current;
    setLoading(true); setError(false);
    try {
      const result = await fetchAutoRenewRequests({ ...filters, continuation: page });
      if (ticket !== sequence.current) return false;
      setData(result); onCount(result); return true;
    } catch (_) {
      if (ticket === sequence.current) { setError(true); setData(null); onCountUnavailable(); }
      return false;
    } finally { if (ticket === sequence.current) setLoading(false); }
  }, [filters, cursor, onCount, onCountUnavailable]);
  useEffect(() => {
    load();
    return () => { sequence.current += 1; };
  }, [load]);
  const changeFilters = next => { if (!locked) { setCursor(null); setHistory([]); setFilters(next); } };
  const refreshQueue = async () => {
    // The server owns both totals. No optimistic removal or decrement. Retain
    // the drawer receipt if this read fails; preserve the selected page.
    return load(cursor);
  };
  const close = useCallback(() => {
    returnFocusId.current = selected?.id ?? null;
    setSelected(null);
  }, [selected]);
  return <div className="ar-request-management" style={{ ...scopeTheme(theme), fontSize: 13 }}>
    <style>{scopeStyles}</style>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
      <label>Request status <select aria-label="Request status" value={filters.status} disabled={locked} onChange={event => changeFilters({ ...filters, status: event.target.value })}>
        <option value="needs_action">Needs action</option><option value="all">All</option>
        {["Requested", "In Progress", "Completed", "Cancelled"].map(value => <option key={value}>{value}</option>)}
      </select></label>
      <label>Notification <select aria-label="Notification" value={filters.notification} disabled={locked} onChange={event => changeFilters({ ...filters, notification: event.target.value })}>
        <option value="all">All notifications</option><option value="issues">Pending or failed</option>
        {["Pending", "Sent", "Failed"].map(value => <option key={value}>{value}</option>)}
      </select></label>
      <button disabled={locked} onClick={() => changeFilters({ ...filters, status: "all", notification: "issues" })}>Notification issues</button>
      <button ref={refreshButtonRef} disabled={loading || locked} onClick={refreshQueue}>Refresh requests</button>
    </div>
    <form onSubmit={event => { event.preventDefault(); changeFilters({ ...filters, search }); }} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
      <input aria-label="Search requests" placeholder="Customer, requester or request number…" value={search} disabled={locked} onChange={event => setSearch(event.target.value)} style={{ width: "min(340px, 80%)" }} />{" "}
      <button disabled={locked}>Search</button>
    </form>
    <p ref={fallbackFocusRef} tabIndex={-1} style={{ color: theme.muted }}>One row per recorded request. Completion records paperwork for the captured next term; notification is a separate status.</p>
    {loading ? <p role="status">Loading requests…</p> : error ? <p role="alert">Requests and counts are unavailable. Refresh to try again.</p> : !data?.enabled ?
      <p>Auto-renew request management is not enabled.</p> : <>
        <p>{validCount(data.total_count) ? `${data.total_count} ${filters.status === "needs_action" && filters.notification === "all" && !filters.search ? "outstanding requests" : "matching requests"}${filters.status === "all" && filters.notification === "all" && !filters.search ? " in all history" : ""}` : "Result count unavailable"}</p>
        {data.items?.length ? <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
          <thead><tr>{["Customer", "Requested by", "Requested (America/Chicago)", "Captured term end", "Request status", "Notification"].map(label => <th key={label} style={{ padding: 8, borderBottom: `1px solid ${theme.border}` }}>{label}</th>)}</tr></thead>
          <tbody>{data.items.map(row => <tr key={row.id}>
            <td style={{ padding: 8 }}><button ref={element => { if (element) rowTriggers.current.set(row.id, element); else rowTriggers.current.delete(row.id); }} disabled={locked} onClick={() => setSelected({ id: row.id })}>{row.customer}</button><small style={{ display: "block", color: theme.muted }}>{row.request_number || "Request number unavailable"}</small></td>
            <td style={{ padding: 8 }}>{row.requested_by_name || "Unavailable"}<small style={{ display: "block", color: theme.muted }}>{row.requested_by_email}</small></td>
            <td style={{ padding: 8 }}>{instant(row.requested_on)}</td><td style={{ padding: 8 }}>{fmtDateOnly(row.term_end)}</td>
            <td style={{ padding: 8 }}>{row.status}{!!row.needs_review?.length && <small style={{ display: "block" }} title={row.needs_review.join("; ")}>Needs review</small>}</td><td style={{ padding: 8 }}>{row.notification}</td>
          </tr>)}</tbody>
        </table></div> : <p>{filters.status === "needs_action" && !filters.search && filters.notification === "all" ? "No outstanding requests." : "No requests match these filters."}</p>}
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button disabled={locked || !history.length} onClick={() => { setCursor(history[history.length - 1]); setHistory(values => values.slice(0, -1)); }}>Previous page</button>
          <button disabled={locked || !data.continuation} onClick={() => { setHistory(values => [...values, cursor]); setCursor(data.continuation); }}>Next page</button>
        </div>
      </>}
    {selected && <AutoRenewRequestDrawer key={selected.id} requestId={selected.id} theme={theme} onClose={close} onLockChange={setLock} onCompleted={refreshQueue} />}
  </div>;
}
